import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, ExternalLink, Trash2, Image as ImageIcon, Calendar, Heart, MessageSquare, BarChart3, FolderTree, Upload } from 'lucide-react';
import { adminApi } from '../api';
import CreateSubgalleryDialog from '../components/CreateSubgalleryDialog';
import AddPhotosDialog from '../components/AddPhotosDialog';
import GalleryStatsDialog from '../components/GalleryStatsDialog';
import BrandLogo from '../components/BrandLogo';
import InlineTitleEdit from '../components/InlineTitleEdit';
import { toast } from 'sonner';

const AdminGallery = () => {
  const { clientSlug, gallerySlug } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [addPhotosSub, setAddPhotosSub] = useState(null); // { id, title }
  const [statsOpen, setStatsOpen] = useState(false);

  const load = async () => {
    try {
      const c = await adminApi.getClient(clientSlug);
      setClient(c);
      const g = c.galleries.find(x => x.slug === gallerySlug);
      if (!g) { toast.error('Lavoro non trovato'); nav(`/admin/clienti/${clientSlug}`); return; }
      setGallery(g);
      const list = await adminApi.listSubgalleries(g.id);
      setSubs(list);
    } catch (err) { toast.error('Errore caricamento'); nav('/admin'); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientSlug, gallerySlug]);

  const removeSub = async (s) => {
    if (!window.confirm(`Eliminare la sotto-galleria "${s.title}"?`)) return;
    try { await adminApi.deleteSubgallery(s.id); await load(); toast.success('Sotto-galleria eliminata'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  if (!client || !gallery) return null;
  const totalPhotos = subs.reduce((s, x) => s + (x.photo_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
          <Link to={`/admin/clienti/${clientSlug}`} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> {client.name}</Link>
          <div className="h-4 w-px bg-white/10" />
          <BrandLogo size="sm" />
          <div className="ml-auto text-xs text-slate-400 hidden sm:block">Sotto-gallerie</div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[#C9A961] uppercase tracking-[0.25em] text-xs font-semibold flex items-center gap-2"><FolderTree className="w-3.5 h-3.5" /> Lavoro · {gallery.category} · {gallery.date}</p>
            <h1 className="font-display text-4xl md:text-5xl mt-1">
              <InlineTitleEdit
                value={gallery.title}
                dataTestId="gallery-title"
                onSave={async (t) => { await adminApi.updateGallery(gallery.id, { title: t }); toast.success('Titolo lavoro aggiornato'); await load(); }}
              />
            </h1>
            <p className="text-slate-400 mt-1">{subs.length} sotto-{subs.length===1?'galleria':'gallerie'} · {totalPhotos} foto totali</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>setStatsOpen(true)} className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-xs px-3 py-2 rounded-full"><BarChart3 className="w-3.5 h-3.5" /> Statistiche lavoro</button>
            <button onClick={()=>setOpen(true)} className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-white text-black font-semibold px-5 py-2.5 rounded-full transition"><Plus className="w-4 h-4" /> Nuova sotto-galleria</button>
          </div>
        </div>

        {subs.length === 0 ? (
          <div className="bg-white/5 border-2 border-dashed border-white/10 rounded-2xl p-16 text-center">
            <FolderTree className="w-10 h-10 text-[#C9A961] mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">Nessuna sotto-galleria</h3>
            <p className="text-slate-400 mb-6 text-sm">Aggiungi la prima cartella di foto (es. Preparativi, Cerimonia, Ricevimento).</p>
            <button onClick={()=>setOpen(true)} className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-white text-black font-semibold px-5 py-2.5 rounded-full transition"><Plus className="w-4 h-4" /> Nuova sotto-galleria</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {subs.map((s, idx) => (
              <div key={s.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#C9A961]/50 transition-all flex flex-col">
                <Link to={`/admin/clienti/${clientSlug}/lavori/${gallerySlug}/sotto/${s.slug}`} className="aspect-[4/3] relative overflow-hidden bg-black block group">
                  {s.cover_url ? <img src={s.cover_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon /></div>}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition" />
                  <div className="absolute top-3 left-3 bg-black/70 backdrop-blur rounded-full px-3 py-1 text-[10px] uppercase tracking-widest text-[#C9A961]">Sotto {idx+1}</div>
                </Link>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-display text-xl">
                    <InlineTitleEdit
                      value={s.title}
                      dataTestId={`subgallery-title-${s.id}`}
                      onSave={async (t) => { await adminApi.updateSubgallery(s.id, { title: t }); toast.success('Titolo sotto-galleria aggiornato'); await load(); }}
                    />
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
                    <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {s.photo_count}</span>
                    <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {s.like_count}</span>
                    <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {s.comment_count}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <Link to={`/admin/clienti/${clientSlug}/lavori/${gallerySlug}/sotto/${s.slug}`} className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs font-semibold px-3 py-2 rounded-full transition"><FolderTree className="w-3.5 h-3.5" /> Gestisci</Link>
                    <button onClick={()=>setAddPhotosSub({ id: s.id, title: s.title })} className="inline-flex items-center justify-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-3 py-2 rounded-full transition"><Upload className="w-3.5 h-3.5" /> Aggiungi foto</button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <a href={`/c/${clientSlug}/lavori/${gallerySlug}/${s.slug}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-full" title="Anteprima cliente"><ExternalLink className="w-3 h-3" /> Anteprima</a>
                    <button onClick={()=>removeSub(s)} className="text-slate-500 hover:text-rose-400 p-1.5" title="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateSubgalleryDialog open={open} onOpenChange={setOpen} galleryId={gallery.id} onCreated={load} />
      {addPhotosSub && (
        <AddPhotosDialog
          open={!!addPhotosSub}
          onOpenChange={(v)=>{ if (!v) setAddPhotosSub(null); }}
          subgalleryId={addPhotosSub.id}
          subgalleryTitle={addPhotosSub.title}
          onAdded={load}
        />
      )}
      {statsOpen && <GalleryStatsDialog galleryId={gallery.id} onClose={()=>setStatsOpen(false)} />}
    </div>
  );
};

export default AdminGallery;
